import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { notification, Form } from "antd";
import type { UploadFile } from "antd/es/upload/interface";
import { handleMessageError } from "@lhd/features/Apps/utils";
import axiosInstanceV2 from "@lhd/libs/axiosV2";
import axiosInstance from "@lhd/libs/axios";
import { chatCatalogRequest } from "../../../../hooks/chatCatalog";
import { SERVICES } from "@lhd/constants";
import { useMarkThreadAsRead } from "@lhd/hooks";
import { CABLE_RECONNECTED_EVENT, getSharedCable } from "@lhd/libs/actionCable";
import { useSelector } from "react-redux";
import { user } from "@lhd/features/users/selector";
import { getCookie, getCompanyId } from "@lhd/utility";
import {
  AiState,
  AiStateSnapshot,
  AiStatusEvent,
  AiRunTracker,
  applyAiStateSnapshot,
  applyAiStatusEvent,
  createAiRunTracker,
  getAiStateFromEvent,
  getDisplayedAiState,
  normalizeResponseMode,
  ResponseMode,
} from "./aiStatus";
import {
  getOrCreateMessageSubmission,
  MessageSubmissionCache,
  MessageSubmissionFile,
} from "./messageSubmission";
import { mergeThreadMetadata, threadMetadataFromEvent } from "./threadMetadata";
export { mergeThreadMetadata, threadMetadataFromEvent } from "./threadMetadata";

export type ReceivedProps = {
  messages?: any;
  isAdmin?: boolean;
  handleBack?: any;
  threadId: string;
  userId: string;
  data: any;
  onDirtyChange?: (isDirty: boolean) => void;
  isFullscreen?: boolean;
  onRefetchUnreadList?: () => void;
};
export interface FormValues {
  title: string;
  content: string;
  images?: UploadFile<File>[];
  response_mode?: ResponseMode;
  inquiry_type?: string | null;
}

export interface IOption {
  label: any;
  value: string;
  color?: string;
}

export interface IThread {
  created_by?: string;
  company_name?: string;
  company_id?: string | number | null;
  created_at?: string;
  updated_at?: string;
  status_id?: string;
  id?: string;
  title?: string;
  inquiry_type?: string | null;
  response_mode?: ResponseMode | null;
  ai_state?: AiState | AiStateSnapshot | null;
}

const useModalChat = (props: ReceivedProps) => {
  const [form] = Form.useForm<FormValues>();
  const { markAsRead } = useMarkThreadAsRead();
  const threadId = props?.threadId;
  const onRefetchUnreadList = props?.onRefetchUnreadList;
  const onDirtyChange = props?.onDirtyChange;
  const initialResponseMode = props?.data?.response_mode;
  const recordCompanyId =
    props?.data?.company_id ??
    props?.data?.companyId ??
    props?.data?.thread?.company_id;
  const threadDetailRef = useRef<IThread>({});
  const propsDataRef = useRef(props?.data);
  const lastFetchedCatalogCompanyIdRef = useRef<string | number | null>(null);

  const [messages, setMessages] = useState<any[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<UploadFile<File>[]>([]);
  const [messageText, setMessageText] = useState("");
  const [isLoading, setListLoading] = useState(false);
  const [statusOptions, setStatusOptions] = useState<IOption[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<IOption[]>([]);
  const [superAdminOptions, setSuperAdminOptions] = useState<IOption[]>([]);
  const [threadDetail, setThreadDetail] = useState<IThread>({});
  const [aiRuns, setAiRuns] = useState<AiRunTracker>(() =>
    createAiRunTracker(props?.data?.ai_state),
  );
  const [responseMode, setResponseMode] = useState<ResponseMode>(
    normalizeResponseMode(props?.data?.response_mode),
  );
  const [isUpdatingResponseMode, setIsUpdatingResponseMode] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const isLoadingMoreRef = useRef(false);
  const previousScrollHeightRef = useRef(0);
  const previousScrollTopRef = useRef(0);
  const isSubmittingRef = useRef(false);
  const auth = useSelector(user);
  const isAdmin =
    !!auth?.is_admin &&
    (!!auth?.workers?.some((worker) => worker?.company?.is_admin) ||
      !!auth?.is_root_company);

  const cableRef = useRef<any>(null);
  const subscriptionRef = useRef<any>(null);
  const chatContentRef = useRef<HTMLDivElement>(null);
  const pendingMessageSubmissionRef = useRef<MessageSubmissionCache | null>(
    null,
  );

  // Refs để tránh stale closure trong WebSocket handler
  const currentPageRef = useRef(currentPage);
  const handleGetThreadMessageDetailRef = useRef<
    ((page?: number, isLoadMore?: boolean) => Promise<void>) | null
  >(null);
  const handleGetThreadDetailRef = useRef<(() => Promise<void>) | null>(null);
  const aiStateVersionRef = useRef(0);

  const WS_URL = (process.env.REACT_APP_API_URL || "")
    .replace("/api/v1", "")
    .replace(/^https?:\/\//, "");
  const accessToken = getCookie("access_token") || "";
  const uid = localStorage.getItem("uid");
  const client = localStorage.getItem("client");

  const sanitizedThreadId = props?.threadId
    ? String(props.threadId).replace("#", "")
    : "";

  const scrollToBottom = () => {
    if (!chatContentRef.current) return;

    const el = chatContentRef.current;

    requestAnimationFrame(() => {
      el.scrollTo({
        top: el.scrollHeight,
        behavior: "auto",
      });
    });
  };

  const appendMessage = React.useCallback((msg: any) => {
    if (!msg || !msg.id) return;
    setMessages((prev) => {
      if (prev.find((m) => m.id === msg.id)) return prev;
      return [...prev, msg];
    });
  }, []);

  const handleGetThreadMessageDetail = React.useCallback(
    async (page = 1, isLoadMore = false) => {
      if (!threadId) return;
      // Synchronous guard with ref to prevent double fetch race conditions
      if (isLoadMore && (!hasMoreMessages || isLoadingMoreRef.current)) return;

      try {
        if (isLoadMore) {
          isLoadingMoreRef.current = true;
          setIsLoadingMore(true);
          // Save both scroll height and scroll position before loading more
          if (chatContentRef.current) {
            previousScrollHeightRef.current =
              chatContentRef.current.scrollHeight;
            previousScrollTopRef.current = chatContentRef.current.scrollTop;
          }
        } else {
          setListLoading(true);
        }

        const PER_PAGE = 15;
        const { data } = await axiosInstanceV2({
          method: "get",
          url: `${SERVICES.THREADS}/${sanitizedThreadId}/messages`,
          params: {
            page,
            per_page: PER_PAGE,
          },
        });

        // Reverse the data because API returns sorted by created_at desc
        const reversedData = [...data].reverse();

        if (isLoadMore) {
          // Prepend older messages to the beginning
          setMessages((prev) => [...reversedData, ...prev]);

          // Restore scroll position after new messages are added
          requestAnimationFrame(() => {
            if (chatContentRef.current) {
              const newScrollHeight = chatContentRef.current.scrollHeight;
              const scrollDiff =
                newScrollHeight - previousScrollHeightRef.current;
              chatContentRef.current.scrollTop =
                previousScrollTopRef.current + scrollDiff;
            }
            isLoadingMoreRef.current = false;
            setIsLoadingMore(false);
          });
        } else {
          setMessages(reversedData);
          setListLoading(false);
        }

        // Check if there are more messages to load
        if (data.length < PER_PAGE) {
          setHasMoreMessages(false);
        }

        if (!isLoadMore) {
          await markAsRead(sanitizedThreadId);
          onRefetchUnreadList?.();
        }
      } catch (error: any) {
        if (isLoadMore) {
          isLoadingMoreRef.current = false;
          setIsLoadingMore(false);
        } else {
          setListLoading(false);
        }
        notification["error"]({
          message: handleMessageError(error?.response?.data?.error),
        });
      }
    },
    [
      threadId,
      onRefetchUnreadList,
      sanitizedThreadId,
      markAsRead,
      hasMoreMessages,
    ],
  );

  const handleRemoveFile = (uid: string) => {
    setUploadedFiles((prev) => prev.filter((file) => file.uid !== uid));
  };

  const handleUploadFiles = async (files: File[]) => {
    if (files?.length === 0) return;

    const formData = new FormData();

    files.forEach((file) => {
      formData.append("files[][file]", file);
    });

    const res = await axiosInstanceV2.post(
      `${SERVICES.CHAT_ATTACHMENTS}/upload_multiple`,
      formData,
    );

    return res.data;
  };

  const handleUpdateChatThread = async (values: any) => {
    if (!sanitizedThreadId) return;
    try {
      setListLoading(true);
      await axiosInstanceV2({
        method: "patch",
        url: `${SERVICES.CHAT_THREADS}/${sanitizedThreadId}`,
        data: values,
      });
      notification["success"]({
        message: "保存しました",
      });
      window.dispatchEvent(
        new CustomEvent("chatThreadUpdated", { detail: values }),
      );
      setListLoading(false);
    } catch (error: any) {
      setListLoading(false);
      notification["error"]({
        message: handleMessageError(error?.response?.data?.error),
      });
    }
  };

  const handleResponseModeChange = async (nextMode: ResponseMode) => {
    if (!sanitizedThreadId || isUpdatingResponseMode) return;

    const previousMode = responseMode;
    setResponseMode(nextMode);
    setIsUpdatingResponseMode(true);

    try {
      const { data } = await axiosInstanceV2({
        method: "patch",
        url: `${SERVICES.CHAT_THREADS}/${sanitizedThreadId}`,
        data: { response_mode: nextMode },
      });
      const updatedMode = normalizeResponseMode(
        data?.response_mode ?? nextMode,
      );
      setResponseMode(updatedMode);
      setThreadDetail((previous) => ({
        ...previous,
        ...(data || {}),
        response_mode: updatedMode,
      }));
      form.setFieldsValue({ response_mode: updatedMode });
      window.dispatchEvent(
        new CustomEvent("chatThreadUpdated", {
          detail: { ...(data || {}), response_mode: updatedMode },
        }),
      );
      notification["success"]({
        message:
          updatedMode === "ai"
            ? "AI自動回答に切り替えました"
            : "有人対応に切り替えました",
      });
    } catch (error: any) {
      setResponseMode(previousMode);
      form.setFieldsValue({ response_mode: previousMode });
      notification["error"]({
        message: handleMessageError(error?.response?.data?.error),
      });
    } finally {
      setIsUpdatingResponseMode(false);
    }
  };

  const handleSubmit = async () => {
    if (!threadId || isSubmittingRef.current || isLoading) return;
    try {
      isSubmittingRef.current = true;
      setListLoading(true);
      const files = uploadedFiles
        .map((file) => file.originFileObj)
        .filter((file): file is NonNullable<typeof file> => Boolean(file));

      if (!messageText && files.length === 0) {
        return;
      }

      const submission = getOrCreateMessageSubmission(
        pendingMessageSubmissionRef.current,
        messageText || "",
        uploadedFiles as MessageSubmissionFile[],
      );
      pendingMessageSubmissionRef.current = submission;

      const resFiles =
        submission.uploadedAttachments ??
        (await handleUploadFiles(files)) ??
        [];
      submission.uploadedAttachments = resFiles;

      const formData = new FormData();
      formData.append("content", messageText || "");
      formData.append("client_request_id", submission.clientRequestId);
      if (resFiles?.length > 0) {
        resFiles.forEach((file: any) => {
          formData.append("attachments[][file_url]", file?.file_url);
          formData.append("attachments[][content_type]", file?.content_type);
          formData.append("attachments[][file_size]", file?.file_size);
          formData.append("attachments[][filename]", file?.filename);
          formData.append(
            "attachments[][storage_provider]",
            file?.storage_provider,
          );
        });
      }

      if (messageText || resFiles?.length > 0) {
        const res = await axiosInstanceV2({
          method: "post",
          url: `${SERVICES.THREADS}/${sanitizedThreadId}/messages`,
          data: formData,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        pendingMessageSubmissionRef.current = null;
        if (res?.data) {
          appendMessage(res.data);
          setMessageText("");
          setUploadedFiles([]);
          // await markAsRead(sanitizedThreadId);
          onRefetchUnreadList?.();
        }
      }
    } catch (error: any) {
      notification["error"]({
        message: handleMessageError(error?.response?.data?.error),
      });
    } finally {
      isSubmittingRef.current = false;
      setListLoading(false);
    }
  };

  const handleDeleteMessage = React.useCallback(
    async (messageId: string) => {
      if (!messageId) return;
      try {
        setListLoading(true);
        await axiosInstanceV2({
          method: "delete",
          url: `${SERVICES.THREADS}/${sanitizedThreadId}/messages/${messageId}`,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        setListLoading(false);
      } catch (error: any) {
        setListLoading(false);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.error),
        });
      }
    },
    [sanitizedThreadId],
  );

  const handleGetStatuses = React.useCallback(
    async (targetCompanyId?: string | number | null) => {
      const cid =
        targetCompanyId ??
        threadDetailRef.current?.company_id ??
        propsDataRef.current?.company_id ??
        propsDataRef.current?.companyId ??
        propsDataRef.current?.thread?.company_id ??
        getCompanyId();
      if (!cid) return;
      try {
        setListLoading(true);
        const { data } = await axiosInstance(chatCatalogRequest("status", cid));
        const options = data?.map((item: any) => ({
          label: React.createElement(
            "span",
            { style: { color: item?.color } },
            item?.name,
          ),
          value: item?.id,
          color: item?.color,
        }));
        setStatusOptions(options);
        setListLoading(false);
      } catch (error: any) {
        setListLoading(false);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.error),
        });
      }
    },
    [],
  );

  const handleGetCategories = React.useCallback(
    async (targetCompanyId?: string | number | null) => {
      const cid =
        targetCompanyId ??
        threadDetailRef.current?.company_id ??
        propsDataRef.current?.company_id ??
        propsDataRef.current?.companyId ??
        propsDataRef.current?.thread?.company_id ??
        getCompanyId();
      if (!cid) return;
      try {
        setListLoading(true);
        const { data } = await axiosInstance(
          chatCatalogRequest("category", cid),
        );
        const options = data?.map((item: any) => ({
          label: item?.name,
          value: item?.id,
        }));
        setCategoryOptions(options);
        setListLoading(false);
      } catch (error: any) {
        setListLoading(false);
        notification["error"]({
          message: handleMessageError(error?.response?.data?.error),
        });
      }
    },
    [],
  );

  const fetchCatalogsForCompany = React.useCallback(
    async (targetCompanyId?: string | number | null) => {
      if (!targetCompanyId) return;
      if (
        lastFetchedCatalogCompanyIdRef.current !== null &&
        String(lastFetchedCatalogCompanyIdRef.current) ===
          String(targetCompanyId)
      ) {
        return;
      }
      lastFetchedCatalogCompanyIdRef.current = targetCompanyId;
      await Promise.all([
        handleGetStatuses(targetCompanyId),
        handleGetCategories(targetCompanyId),
      ]);
    },
    [handleGetStatuses, handleGetCategories],
  );

  const fetchCatalogsForCompanyRef = useRef(fetchCatalogsForCompany);
  useEffect(() => {
    fetchCatalogsForCompanyRef.current = fetchCatalogsForCompany;
  }, [fetchCatalogsForCompany]);

  const handleGetSuperAdmin = React.useCallback(async () => {
    try {
      setListLoading(true);
      const { data } = await axiosInstanceV2({
        method: "get",
        url: `${SERVICES.USERS}/super_admins`,
      });
      const options = data?.map((item: any) => ({
        label: item?.full_name,
        value: item?.id,
      }));
      setSuperAdminOptions(options);
      setListLoading(false);
    } catch (error: any) {
      setListLoading(false);
      notification["error"]({
        message: handleMessageError(error?.response?.data?.error),
      });
    }
  }, []);

  const handleGetThreadDetail = React.useCallback(async () => {
    if (!sanitizedThreadId) return;
    const stateVersionAtRequest = aiStateVersionRef.current;
    try {
      setListLoading(true);
      const { data } = await axiosInstanceV2({
        method: "get",
        url: `${SERVICES.CHAT_THREADS}/${sanitizedThreadId}`,
      });
      form.setFieldsValue(data);
      setThreadDetail(data);
      setResponseMode(normalizeResponseMode(data?.response_mode));
      if (stateVersionAtRequest === aiStateVersionRef.current) {
        setAiRuns(applyAiStateSnapshot(data?.ai_state));
      }

      // Fetch status and category options based on the chat record's company_id
      const recordCompanyId = data?.company_id ?? getCompanyId();
      if (recordCompanyId) {
        fetchCatalogsForCompanyRef.current(recordCompanyId);
      }

      setListLoading(false);
    } catch (error: any) {
      setListLoading(false);
      notification["error"]({
        message: handleMessageError(error?.response?.data?.error),
      });
    }
  }, [sanitizedThreadId, form]);

  const lastDirtyRef = useRef(false);

  useEffect(() => {
    threadDetailRef.current = threadDetail;
  }, [threadDetail]);

  useEffect(() => {
    propsDataRef.current = props?.data;
  }, [props?.data]);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    handleGetThreadMessageDetailRef.current = handleGetThreadMessageDetail;
  }, [handleGetThreadMessageDetail]);

  useEffect(() => {
    handleGetThreadDetailRef.current = handleGetThreadDetail;
  }, [handleGetThreadDetail]);

  useEffect(() => {
    const isDirty = messageText.trim() !== "" || uploadedFiles.length > 0;
    if (isDirty !== lastDirtyRef.current) {
      lastDirtyRef.current = isDirty;
      onDirtyChange?.(isDirty);
    }
  }, [messageText, uploadedFiles, onDirtyChange]);

  useEffect(() => {
    setMessages([]);
    setCurrentPage(1);
    setHasMoreMessages(true);
    isLoadingMoreRef.current = false;
    isSubmittingRef.current = false;
    aiStateVersionRef.current += 1;
    pendingMessageSubmissionRef.current = null;
    lastFetchedCatalogCompanyIdRef.current = null;
    setStatusOptions([]);
    setCategoryOptions([]);
    setAiRuns(createAiRunTracker());
    setResponseMode(normalizeResponseMode(initialResponseMode));
    handleGetThreadMessageDetailRef.current?.(1, false);
    handleGetSuperAdmin();

    const initialCompanyId =
      propsDataRef.current?.company_id ??
      propsDataRef.current?.companyId ??
      propsDataRef.current?.thread?.company_id;

    if (initialCompanyId) {
      fetchCatalogsForCompanyRef.current(initialCompanyId);
    }

    handleGetThreadDetailRef.current?.();
  }, [threadId, initialResponseMode, handleGetSuperAdmin]);

  // Sync catalog options if company_id is provided or updated via props
  useEffect(() => {
    if (recordCompanyId) {
      fetchCatalogsForCompanyRef.current(recordCompanyId);
    }
  }, [recordCompanyId]);

  useEffect(() => {
    if (!sanitizedThreadId) return;

    const setupSubscription = (isReconnect = false) => {
      const cable = getSharedCable();
      if (!cable) return;

      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }

      subscriptionRef.current = cable.subscriptions.create(
        {
          channel: "ChatThreadChannel",
          thread_id: sanitizedThreadId,
        },
        {
          received: (data: any) => {
            switch (data.event) {
              case "created":
                if (data.message) {
                  appendMessage(data.message);
                  markAsRead(sanitizedThreadId);
                }
                break;

              case "updated":
                if (data.message?.id) {
                  setMessages((prev) =>
                    prev.map((message) =>
                      message.id === data.message.id
                        ? { ...message, ...data.message }
                        : message,
                    ),
                  );
                }
                break;

              case "thread_updated": {
                if (
                  data.thread_id !== undefined &&
                  String(data.thread_id) !== String(sanitizedThreadId)
                ) {
                  break;
                }
                const metadata = threadMetadataFromEvent(data);
                setThreadDetail((previous) =>
                  mergeThreadMetadata(previous, metadata),
                );
                if (Object.keys(metadata).length > 0) {
                  form.setFieldsValue(metadata);
                }
                window.dispatchEvent(
                  new CustomEvent("chatThreadUpdated", { detail: data }),
                );
                break;
              }

              case "ai_status": {
                if (
                  data.thread_id !== undefined &&
                  String(data.thread_id) !== String(sanitizedThreadId)
                ) {
                  break;
                }
                const nextState = getAiStateFromEvent(data as AiStatusEvent);
                if (nextState !== undefined) {
                  aiStateVersionRef.current += 1;
                  setAiRuns((current) =>
                    applyAiStatusEvent(current, data as AiStatusEvent),
                  );
                }
                break;
              }

              case "deleted":
                if (data.message_id) {
                  const formattedTime = data.soft_deleted_at
                    ? dayjs(data.soft_deleted_at)
                        .utcOffset(9)
                        .format("YYYY/MM/DD HH:mm")
                    : "";
                  skipScrollOnDeleteRef.current = true;
                  setMessages((prev) => {
                    return prev.flatMap((msg) => {
                      if (msg.id !== data.message_id) return [msg];
                      const deletedText = `本メッセージは ${formattedTime} に削除されました`;
                      if (data.deleted_by_super_admin) {
                        if (isAdmin) {
                          return [
                            {
                              ...msg,
                              content: deletedText,
                              soft_deleted_at: data.soft_deleted_at,
                              deleted_by_super_admin: true,
                              attachments: [],
                            },
                          ];
                        } else {
                          return [];
                        }
                      } else {
                        if (!isAdmin) {
                          return [
                            {
                              ...msg,
                              content: deletedText,
                              soft_deleted_at: data.soft_deleted_at,
                              attachments: [],
                            },
                          ];
                        } else {
                          return [msg];
                        }
                      }
                    });
                  });
                }
                break;

              default:
                console.warn("Unknown realtime event:", data);
            }
          },
        },
      );

      if (isReconnect) {
        // ActionCable may reconnect after status/message events were missed.
        // Refetch both sources so the server's current state is authoritative.
        handleGetThreadDetailRef.current?.();
        handleGetThreadMessageDetailRef.current?.(1, false);
      }
    };

    setupSubscription();
    const handleCableReconnect = () => setupSubscription(true);
    window.addEventListener(CABLE_RECONNECTED_EVENT, handleCableReconnect);

    return () => {
      window.removeEventListener(CABLE_RECONNECTED_EVENT, handleCableReconnect);
      subscriptionRef.current?.unsubscribe();
      subscriptionRef.current = null;
    };
  }, [sanitizedThreadId, markAsRead, appendMessage, isAdmin, form]);
  useLayoutEffect(() => {
    if (!isLoadingMoreRef.current && !skipScrollOnDeleteRef.current) {
      scrollToBottom();
    }
    if (skipScrollOnDeleteRef.current) {
      skipScrollOnDeleteRef.current = false;
    }
  }, [messages]);

  // Handle scroll event for lazy loading
  useEffect(() => {
    const chatContent = chatContentRef.current;
    if (!chatContent) return;

    const handleScroll = () => {
      // Check if user scrolled near the top (within 100px)
      if (
        chatContent.scrollTop < 100 &&
        hasMoreMessages &&
        !isLoadingMoreRef.current
      ) {
        const nextPage = currentPage + 1;
        setCurrentPage(nextPage);
        handleGetThreadMessageDetail(nextPage, true);
      }
    };

    chatContent.addEventListener("scroll", handleScroll);
    return () => {
      chatContent.removeEventListener("scroll", handleScroll);
    };
  }, [currentPage, hasMoreMessages, handleGetThreadMessageDetail]);

  const skipScrollOnDeleteRef = useRef(false);

  return {
    ...props,
    handleGetThreadMessageDetail,
    messages,
    appendMessage,
    handleSubmit,
    form,
    uploadedFiles,
    setUploadedFiles,
    messageText,
    setMessageText,
    cableRef,
    subscriptionRef,
    WS_URL,
    accessToken,
    uid,
    client,
    isLoading,
    scrollToBottom,
    chatContentRef,
    handleDeleteMessage,
    handleGetStatuses,
    statusOptions,
    handleGetCategories,
    categoryOptions,
    markAsRead,
    handleRemoveFile,
    superAdminOptions,
    sanitizedThreadId,
    renderedMessages: messages,
    threadDetail,
    handleUpdateChatThread,
    handleResponseModeChange,
    responseMode,
    isUpdatingResponseMode,
    aiRuns,
    aiState: getDisplayedAiState(aiRuns),
    isLoadingMore,
    hasMoreMessages,
    skipScrollOnDeleteRef,
  };
};

export type Props = ReturnType<typeof useModalChat>;

export default useModalChat;
